// src/components/calendar/EventDetailPanel/EventDetailPanel.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Trash2, ExternalLink, Loader2, MapPin, Wrench, User, Clock, Calendar } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Badge } from '../../ui/badge';

const EventDetailPanel = ({ event, onClose, deletingEvent, onDelete }) => {
  const navigate = useNavigate();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const isGeneral = event?.event_category === 'general';

  // Keyboard handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.isContentEditable
      ) return;

      if (e.key === 'Escape') {
        if (confirmingDelete) {
          setConfirmingDelete(false);
        } else {
          onClose();
        }
        return;
      }

      if ((e.key === 'Delete' || e.key === 'Backspace') && isGeneral && !confirmingDelete) {
        setConfirmingDelete(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [confirmingDelete, isGeneral, onClose]);

  // Reset confirm state when event changes
  useEffect(() => {
    setConfirmingDelete(false);
  }, [event?.id]);

  if (!event) return null;

  // Format date/time from event.start (Date object)
  const formatDateTime = (date) => {
    if (!date) return '—';
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const durationMinutes = event.duration_minutes ||
    (event.end ? Math.round((event.end.getTime() - event.start.getTime()) / (1000 * 60)) : null);

  const formatDuration = (minutes) => {
    if (!minutes) return '—';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  };

  const getBlockingLabel = () => {
    if (event.blocking_type === 'capacity') return 'Capacity Block';
    return 'Full Block';
  };

  const getStatusColor = (status) => {
    const map = {
      pending: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      confirmed: 'bg-green-500/20 text-green-300 border-green-500/30',
      completed: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      cancelled: 'bg-red-500/20 text-red-300 border-red-500/30',
    };
    return map[status] || 'bg-slate-500/20 text-slate-300 border-slate-500/30';
  };

  return (
    <div className="flex flex-col h-full bg-slate-900/95 backdrop-blur-xl">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/50">
        <h2 className="text-lg font-semibold text-slate-100 truncate pr-2">
          {event.title || 'Event Details'}
        </h2>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 transition-colors flex-shrink-0"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
        {isGeneral ? (
          // General event details
          <>
            <div className="space-y-3">
              {/* Date/Time */}
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-0.5">Date / Time</p>
                  <p className="text-sm text-slate-200">{formatDateTime(event.start)}</p>
                </div>
              </div>

              {/* Duration */}
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-0.5">Duration</p>
                  <p className="text-sm text-slate-200">{formatDuration(durationMinutes)}</p>
                </div>
              </div>

              {/* Blocking type */}
              <div className="flex items-start gap-3">
                <div className="w-4 h-4 flex-shrink-0 mt-0.5 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-slate-400" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Blocking</p>
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-xs border',
                      event.blocking_type === 'capacity'
                        ? 'bg-orange-500/20 text-orange-300 border-orange-500/30'
                        : 'bg-red-500/20 text-red-300 border-red-500/30'
                    )}
                  >
                    {getBlockingLabel()}
                  </Badge>
                </div>
              </div>

              {/* Description */}
              {event.description && (
                <div className="flex items-start gap-3">
                  <div className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-0.5">Description</p>
                    <p className="text-sm text-slate-300 whitespace-pre-wrap">{event.description}</p>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          // Service event details
          <>
            <div className="space-y-3">
              {/* Customer */}
              {event.customer?.name && (
                <div className="flex items-start gap-3">
                  <User className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-0.5">Customer</p>
                    <p className="text-sm text-slate-200">{event.customer.name}</p>
                  </div>
                </div>
              )}

              {/* Service type */}
              {event.service_type && (
                <div className="flex items-start gap-3">
                  <Wrench className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-0.5">Service</p>
                    <p className="text-sm text-slate-200">{event.service_type}</p>
                  </div>
                </div>
              )}

              {/* Date/Time */}
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-0.5">Date / Time</p>
                  <p className="text-sm text-slate-200">{formatDateTime(event.start)}</p>
                </div>
              </div>

              {/* Duration */}
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-0.5">Duration</p>
                  <p className="text-sm text-slate-200">{formatDuration(durationMinutes)}</p>
                </div>
              </div>

              {/* Status */}
              {event.status && (
                <div className="flex items-start gap-3">
                  <div className="w-4 h-4 flex-shrink-0 mt-0.5 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-slate-400" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Status</p>
                    <Badge
                      variant="outline"
                      className={cn('text-xs border capitalize', getStatusColor(event.status))}
                    >
                      {event.status}
                    </Badge>
                  </div>
                </div>
              )}

              {/* Address */}
              {event.service_address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-0.5">Address</p>
                    <p className="text-sm text-slate-300">{event.service_address}</p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-slate-700/50 flex gap-3">
        {isGeneral ? (
          confirmingDelete ? (
            // Confirmation state
            <>
              <button
                onClick={() => setConfirmingDelete(false)}
                disabled={deletingEvent}
                className="flex-1 px-4 py-2 border border-slate-600/50 text-slate-300 rounded-lg text-sm hover:bg-slate-700/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Keep Event
              </button>
              <button
                onClick={() => onDelete(event.id)}
                disabled={deletingEvent}
                className={cn(
                  'flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2',
                  deletingEvent
                    ? 'bg-red-700/50 text-red-300 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-500 text-white'
                )}
              >
                {deletingEvent ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting…
                  </>
                ) : (
                  'Yes, Delete'
                )}
              </button>
            </>
          ) : (
            // Normal state
            <>
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-slate-600/50 text-slate-300 rounded-lg text-sm hover:bg-slate-700/30 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => setConfirmingDelete(true)}
                className="flex-1 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 text-red-300 hover:text-red-200 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete Event
              </button>
            </>
          )
        ) : (
          // Service event footer
          <>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-600/50 text-slate-300 rounded-lg text-sm hover:bg-slate-700/30 transition-colors"
            >
              Close
            </button>
            <button
              onClick={() => navigate(`/appointments/${event.id}`)}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              View Appointment
              <ExternalLink className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default EventDetailPanel;
